import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment-timezone';
import { InfoService } from './info.service';

/**
 * Constrói HTML para a área de transferência REPLICANDO 1:1 a técnica do
 * copy da "Lista de submissões" do TPC chair
 * (`submissions-list-table-v2.component.ts` → `buildExportHtmlDoc` + inline).
 *
 * Pontos-chave da técnica do chair (validada):
 *   1. Documento completo `<!doctype><html><head><style>…</style></head><body>…</body></html>`
 *      com bloco `<style>` no head + estilos inline em CADA elemento (defesa em
 *      profundidade — clientes que strippam `<style>` (Gmail web) caem no inline;
 *      clientes que strippam `style=""` (raros) caem no bloco).
 *   2. Uma única `<table>` com `border-collapse:separate;border-spacing:1px 1px`:
 *      o spacing de 1px AUTOMATICAMENTE cria a "linha branca" vertical entre as
 *      colunas e horizontal entre os cards (a página atrás aparece).
 *   3. `background-color` em cada `<td>` (não em `<div>` outer).
 *   4. Borda lateral colorida do status via `background-image:linear-gradient(
 *      to right, COLOR 5px, transparent 5px)` — **não** via `border-left`. Background
 *      gradient sobrevive muito melhor que border-left em destinos rich-text.
 *   5. Cantos arredondados via `border-{top|bottom}-{left|right}-radius:6px` na
 *      PRIMEIRA e ÚLTIMA células de cada linha (não no `<table>`).
 *   6. Chip de status: geometria EXATA do chair (`.chip` width 96 height 22, pill
 *      999px, 4 borders sutis, box-shadow) — funciona em qualquer destino.
 *   7. Font-family declarada em `body`, `td` E no `<div>` wrapper — `<table>` não
 *      herda fonte em vários clientes.
 */
@Injectable({ providedIn: 'root' })
export class SelectionClipboardService {

  constructor(
    private translateService: TranslateService,
    private infoService: InfoService,
  ) {}

  // Meses curtos em PT-BR e EN (sem ponto final — espelha o `datePipe`).
  private readonly MONTHS_PT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  private readonly MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // ─── Tokens de design (theme-aware) ─────────────────────────────────
  // Detectado a cada copy via `detectTheme()` lendo `body.dark-theme`.
  // Mantém getters com nomes UPPER pra que todas as templates string
  // existentes (`${this.TEXT_BASE}` etc.) funcionem sem alteração.
  private _isDark = false;
  private detectTheme(): void {
    this._isDark = (typeof document !== 'undefined')
      && document.body.classList.contains('dark-theme');
  }

  // Light tokens = sistema light-mode (vide `_colors.scss`).
  // Dark tokens = sistema dark-mode (sidebar-admin-*-dark, accent-*-dark).
  private get TEXT_BASE(): string { return this._isDark ? '#c4c2bd' : '#373734'; }
  private get TEXT_MUTED(): string { return this._isDark ? 'rgba(196,194,189,0.65)' : '#78716C'; }
  private get TEXT_ACCENT(): string { return this._isDark ? '#FBBF24' : '#7C2D12'; }
  // Chip de status mantém texto escuro em ambos os modos — os bgs dos
  // status (yellow/green/red/etc.) são pastéis claros nos dois temas.
  private get CHIP_TEXT(): string { return '#434343'; }
  private get BG_CARD(): string { return this._isDark ? '#202020' : '#F1F0EF'; }
  // Fundo do paste = transparente nos dois temas — herda do destino
  // (Mac Mail/Word/Notion). Os spacers de 1px e o body usam isso, então
  // os fios entre colunas/cards mostram a cor de fundo do app destino.
  private get BG_PAGE(): string { return 'transparent'; }
  private get EVENT_TITLE(): string { return this._isDark ? '#c4c2bd' : '#373734'; }
  // Tokens específicos do chip "Rebuttal" no dark — bg menuitem-hover-dark.
  private get REBUTTAL_BG(): string { return this._isDark ? '#34332f' : '#E7E5E4'; }
  private get REBUTTAL_BORDER(): string { return this._isDark ? '#3a3937' : '#E7E5E4'; }

  // Stack idêntica ao chair.
  private readonly FONT_STACK =
    'Geist,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif';

  // ─── Mapas de cor por status (idênticos a `_colors.scss`) ───────────
  private readonly SUBMISSION_STATUS_BG: { [k: string]: string } = {
    pending:   '#FAB65C',
    active:    '#FFFF55',
    accepted:  '#99FF99',
    rejected:  '#FFBDBD',
    withdrawn: '#DDDDDD',
    deleted:   '#DDDDDD',
  };
  private readonly REVIEW_STATUS_BG: { [k: string]: string } = {
    assigned:  '#FFFFFF',
    notified:  '#94C0FB',
    confirmed: '#FFD860',
    declined:  '#C6C6C6',
    reminded:  '#FF8FA0',
    late:      '#FA5B5B',
    delegated: '#FF89C9',
    completed: '#63FCC8',
    draft:     '#AAAAAA',
  };

  // Paths SVG dos Material Symbols FILLED (viewBox `0 -960 960 960`) — os
  // MESMOS ícones que o card renderiza (FILL 1, wght 400). Sistema usa
  // variação FILLED via `font-variation-settings: 'wght' 400, 'FILL' 1`
  // — silhuetas sólidas, sem vão interno. Não confundir com a variante
  // outlined do `EXPORT_STATUS_ICONS` do chair (que tem o "miolo" vazio).
  private readonly STATUS_ICON_PATH: { [k: string]: string } = {
    // ── Submissions
    accepted:  'M720-120H240v-480l280-280 50 50q7 7 11.5 19t4.5 23v14l-44 174h258q32 0 56 24t24 56v80q0 7-2 15t-4 15L794-168q-9 20-30 34t-44 14ZM160-120v-480h80v480h-80Z',
    active:    'M382-240 154-468l57-57 171 171 367-367 57 57-424 424Z',
    rejected:  'M240-840h480v480L440-80l-50-50q-7-7-11.5-19t-4.5-23v-14l44-174H160q-32 0-56-24t-24-56v-80q0-7 2-15t4-15l120-282q9-20 30-34t44-14Zm560 0h-80v480h80v-480Z',
    pending:   'M480-40q-33 0-56.5-23.5T400-120q0-33 23.5-56.5T480-200q33 0 56.5 23.5T560-120q0 33-23.5 56.5T480-40Zm-80-280v-560h160v560H400Z',
    withdrawn: 'M200-80q-33 0-56.5-23.5T120-160v-451q-18-11-29-28.5T80-680v-120q0-33 23.5-56.5T160-880h640q33 0 56.5 23.5T880-800v120q0 23-11 40.5T840-611v451q0 33-23.5 56.5T760-80H200Zm-40-600h640v-120H160v120Zm200 280h240v-80H360v80Z',
    deleted:   'M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm120-160h80v-360h-80v360Zm160 0h80v-360h-80v360Z',
    // ── Reviews (assigned/notified = mail; confirmed = thumb_up; etc.)
    assigned:  'M160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm320-280L160-640v400h640v-400L480-440Zm0-80 320-200H160l320 200ZM160-640v-80 480-400Z',
    notified:  'M160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm320-280L160-640v400h640v-400L480-440Zm0-80 320-200H160l320 200ZM160-640v-80 480-400Z',
    confirmed: 'M720-120H240v-480l280-280 50 50q7 7 11.5 19t4.5 23v14l-44 174h258q32 0 56 24t24 56v80q0 7-2 15t-4 15L794-168q-9 20-30 34t-44 14ZM160-120v-480h80v480h-80Z',
    declined:  'M240-840h480v480L440-80l-50-50q-7-7-11.5-19t-4.5-23v-14l44-174H160q-32 0-56-24t-24-56v-80q0-7 2-15t4-15l120-282q9-20 30-34t44-14Zm560 0h-80v480h80v-480Z',
    completed: 'M382-240 154-468l57-57 171 171 367-367 57 57-424 424Z',
    reminded:  'M480-40q-33 0-56.5-23.5T400-120q0-33 23.5-56.5T480-200q33 0 56.5 23.5T560-120q0 33-23.5 56.5T480-40Zm-80-280v-560h160v560H400Z',
    late:      'M480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm112-192 56-56-128-128v-184h-80v216l152 152Z',
    draft:     'M200-200h57l391-391-57-57-391 391v57Zm-80 80v-170l528-527q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L290-120H120Zm640-584-56-56 56 56Zm-141 85-28-29 57 57-29-28Z',
    delegated: 'M480-480q-66 0-113-47t-47-113q0-66 47-113t113-47q66 0 113 47t47 113q0 66-47 113t-113 47ZM160-160v-112q0-34 17.5-62.5T224-378q62-31 126-46.5T480-440q66 0 130 15.5T736-378q29 15 46.5 43.5T800-272v112H160Z',
  };

  // ─── API pública ────────────────────────────────────────────────────

  async copySubmissions(submissions: any[]): Promise<boolean> {
    if (!submissions?.length) { return false; }
    this.detectTheme();
    const html = this.buildDoc(this.buildSubmissionsBody(submissions));
    const text = this.buildSubmissionsText(submissions);
    return this.writeBoth(html, text);
  }

  async copyReviews(reviews: any[]): Promise<boolean> {
    if (!reviews?.length) { return false; }
    this.detectTheme();
    const html = this.buildDoc(this.buildReviewsBody(reviews));
    const text = this.buildReviewsText(reviews);
    return this.writeBoth(html, text);
  }

  /**
   * Copia uma lista ORDENADA de cards mistos. Cada item declara seu tipo
   * (`'submission'` ou `'review'`) e os cards são renderizados na ordem
   * passada. Cards CONSECUTIVOS do mesmo tipo são agrupados numa única
   * `<table>` (alinhamento entre colunas preservado); quando o tipo muda,
   * um `<div>` de 16px (linha em branco) separa as runs.
   *
   * É a API mais flexível — quem chama controla a ordem. `copyMixed`
   * (mantida por compatibilidade) é um wrapper trivial em cima desta.
   */
  async copyOrdered(items: Array<{ type: 'submission' | 'review', data: any }>): Promise<boolean> {
    if (!items?.length) { return false; }
    this.detectTheme();
    const parts: string[] = [];
    let currentType: 'submission' | 'review' | null = null;
    let currentRows: string[] = [];
    const flush = () => {
      if (currentRows.length) {
        parts.push(this.tableOpen() + currentRows.join('') + this.tableClose());
        currentRows = [];
      }
    };
    for (const it of items) {
      if (currentType !== null && it.type !== currentType) {
        flush();
        parts.push(`<div style="height:16px;line-height:0;font-size:0;">&nbsp;</div>`);
      }
      currentType = it.type;
      currentRows.push(
        it.type === 'submission'
          ? this.submissionRowHtml(it.data)
          : this.reviewRowHtml(it.data)
      );
    }
    flush();
    const text = items.map(it =>
      it.type === 'submission'
        ? this.buildSubmissionsText([it.data])
        : this.buildReviewsText([it.data])
    ).join('\n');
    return this.writeBoth(this.buildDoc(parts.join('')), text);
  }

  /**
   * Wrapper trivial sobre `copyOrdered`: passa primeiro todas as submissões,
   * depois todas as revisões. Use quando a ordem visual já é "subs antes,
   * revs depois". Para ordem interleaved (ex.: event-user-view com 3 seções
   * sub→rev→sub), chame `copyOrdered` diretamente.
   */
  async copyMixed(submissions: any[], reviews: any[]): Promise<boolean> {
    const items: Array<{ type: 'submission' | 'review', data: any }> = [
      ...(submissions || []).map(s => ({ type: 'submission' as const, data: s })),
      ...(reviews || []).map(r => ({ type: 'review' as const, data: r })),
    ];
    return this.copyOrdered(items);
  }

  // ─── Documento (espelha `buildExportHtmlDoc(...inline:true)`) ───────

  private buildDoc(body: string): string {
    // Bloco <style> com as MESMAS regras que os inlines (defesa em profun-
    // didade). Clientes que honram <style> aplicam tudo daqui; quem strippa,
    // cai no inline.
    const styleBlock = ''
      + `body{font-family:${this.FONT_STACK};font-size:13px;color:${this.TEXT_BASE};margin:0;background:${this.BG_PAGE};width:100%}`
      + `h2{font-family:${this.FONT_STACK};font-size:18px;font-weight:600;color:${this.EVENT_TITLE};margin:18px 0 8px;letter-spacing:-0.015em;line-height:1.3}`
      + `table{border-collapse:separate;border-spacing:1px 1px;width:100%}`
      + `td{font-family:${this.FONT_STACK};font-size:13px;color:${this.TEXT_BASE};padding:10px 14px;border:0;vertical-align:middle;text-align:left}`
      + `a{color:${this.TEXT_ACCENT};text-decoration:none !important}`
      + `.chip{display:inline-flex;align-items:center;justify-content:center;min-width:96px;height:22px;padding:0 10px;box-sizing:border-box;border-radius:999px;font-family:${this.FONT_STACK};font-size:11px;font-weight:500;line-height:1;letter-spacing:0;color:#434343;border-top:1px solid rgba(255,255,255,.6);border-left:1px solid rgba(255,255,255,.6);border-right:1px solid transparent;border-bottom:1px solid transparent;box-shadow:0 1px 2px rgba(0,0,0,.55);white-space:nowrap;text-align:center}`;
    // Sem max-width — a coluna main (com width:100%) absorve toda a sobra
    // de largura disponível no destino, minimizando line-breaks no título
    // e nos autores. As demais colunas (ID, sessão, meta) continuam
    // shrink-to-fit via `width:1px` + `white-space:nowrap`.
    const wrapper =
      `<div style="font-family:${this.FONT_STACK};font-size:13px;color:${this.TEXT_BASE};width:100%;">${body}</div>`;
    return ''
      + '<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">'
      + `<title>Itens selecionados</title><style>${styleBlock}</style></head>`
      + `<body>${wrapper}</body></html>`;
  }

  // ─── Bodies (agrupamento por evento como na tela) ───────────────────

  private buildSubmissionsBody(items: any[]): string {
    const rows = items.map(s => this.submissionRowHtml(s)).join('');
    return this.tableOpen() + rows + this.tableClose();
  }

  private buildReviewsBody(items: any[]): string {
    const rows = items.map(r => this.reviewRowHtml(r)).join('');
    return this.tableOpen() + rows + this.tableClose();
  }

  private groupByEvent(items: any[], eventOf: (i: any) => any): Map<number, { event: any, items: any[] }> {
    const map = new Map<number, { event: any, items: any[] }>();
    for (const it of items) {
      const ev = eventOf(it);
      const id = ev?.id ?? -1;
      if (!map.has(id)) { map.set(id, { event: ev, items: [] }); }
      map.get(id).items.push(it);
    }
    return map;
  }

  // ─── Tabela ─────────────────────────────────────────────────────────

  private tableOpen(): string {
    // border-spacing: 0 (horizontal) + 1px (vertical).
    // - Horizontal 0: nenhum gap automático entre células da mesma row
    //   → faixa do status COLA no ID (sem fio espúrio).
    // - Vertical 1px: gap entre rows = espaço entre cards.
    // Os separadores brancos entre ID|Main e Main|Sessão são CÉLULAS
    // explícitas de 1px (`spacerCellHtml`) — funcionam porque ficam na
    // mesma row que o conteúdo (herdam a altura automaticamente).
    //
    return `<table cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:separate;border-spacing:0 1px;width:100%;font-family:${this.FONT_STACK};font-size:13px;color:${this.TEXT_BASE};margin:0 0 12px;">`;
  }

  /**
   * Célula-separadora vertical de 1px branco. Em vez de `border-right`
   * ou `box-shadow` (ambos stripados por vários destinos rich-text em
   * `<td>`), uso uma `<td>` real. Como ela vive na mesma row que as
   * outras células, herda a altura automaticamente (regra básica de
   * tabelas HTML: cells de uma row têm a mesma altura).
   * `font-size:0;line-height:0` neutraliza qualquer ocupação de espaço
   * vertical pelo `&nbsp;`.
   */
  private spacerCellHtml(): string {
    // Spacer transparente — mostra a cor do destino (Mac Mail/Notion/etc.).
    // bgcolor HTML attr omitido propositadamente (não aceita "transparent");
    // só CSS background-color:transparent (ou ausência, mesma coisa).
    return `<td width="1" style="width:1px;min-width:1px;padding:0;font-size:0;line-height:0;background-color:transparent;">&nbsp;</td>`;
  }

  /**
   * Célula-gêmea da faixa do status, mas à DIREITA: 6px de largura, cor
   * do card, com `border-radius` no canto top-right e bottom-right.
   *
   * Por que isso resolve o problema do canto direito não arredondar nos
   * destinos rich-text: a faixa da esquerda É renderizada arredondada
   * porque é uma célula PEQUENA (5px). Vários renderers só honram
   * `border-radius` em células com largura pequena (provavelmente porque
   * o algoritmo de layout calcula a área onde o radius cabe ANTES de
   * aplicar). Replicando o padrão à direita — célula pequena, cor do
   * card, radius direito — o canto arredonda.
   *
   * Visualmente: parece que a coluna direita do card termina arredondada,
   * porque os 6px da célula trailing têm a mesma cor de fundo da coluna
   * adjacente (BG_CARD) — o olho lê como continuação.
   */
  private trailingCornerCellHtml(): string {
    return `<td bgcolor="${this.BG_CARD}" width="6" style="width:6px;min-width:6px;padding:0;font-size:0;line-height:0;background-color:${this.BG_CARD};border-top-right-radius:6px;border-bottom-right-radius:6px;">&nbsp;</td>`;
  }
  private tableClose(): string { return `</table>`; }

  // ─── Linhas (cards) ─────────────────────────────────────────────────

  private submissionRowHtml(s: any): string {
    const statusKey = (s?.status ?? '').toString().toLowerCase();
    const statusBg = this.SUBMISSION_STATUS_BG[statusKey] || '#DDDDDD';
    const hasSession = !!(s?.session && (s.session.title || s.session.start));
    // Estrutura: [bar 5px][ID][spacer][main][(spacer)(session)][trailing 6px]
    // Spacers = células brancas de 1px → fios verticais entre colunas reais.
    // Trailing = célula gêmea da bar mas à direita — encapsula o
    // arredondamento do canto direito (vide `trailingCornerCellHtml`).
    return `<tr>`
      + this.statusBarCellHtml(statusBg)
      + this.idCellHtml(`#${s?.id ?? ''}`, statusKey, statusBg)
      + this.spacerCellHtml()
      + this.mainCellHtml(this.submissionMainContent(s), false)
      + (hasSession
        ? this.spacerCellHtml() + this.sessionCellHtml(s.session, s?.event?.eventInfo?.timeZone, false)
        : '')
      + this.trailingCornerCellHtml()
      + `</tr>`;
  }

  private reviewRowHtml(r: any): string {
    const sub = r?.submission ?? {};
    const subStatusKey = (sub?.status ?? '').toString().toLowerCase();
    const subStatusBg = this.SUBMISSION_STATUS_BG[subStatusKey] || '#DDDDDD';
    // Faixa lateral E círculo do ID = AMBOS cor do status da SUBMISSÃO.
    // Stage + Ownership + Prazo ficam na main cell (vide reviewLineHtml).
    // Discussão + Rebuttal ficam na meta cell (à direita, como no card).
    return `<tr>`
      + this.statusBarCellHtml(subStatusBg)
      + this.idCellHtml(`#${sub?.id ?? ''}`, subStatusKey, subStatusBg)
      + this.spacerCellHtml()
      + this.mainCellHtml(this.reviewMainContent(r), false)
      + this.spacerCellHtml()
      + this.reviewMetaCellHtml(r)
      + this.trailingCornerCellHtml()
      + `</tr>`;
  }

  // ─── Células ────────────────────────────────────────────────────────

  /**
   * Faixa do status — célula DEDICADA de 5px de largura. Usa `bgcolor=""`
   * (atributo HTML legado) ALÉM do CSS `background-color`. Destinos que
   * strippam CSS bg (Notion, Apple Notes) ainda honram `bgcolor`. Essa é
   * a célula que carrega o arredondamento esquerdo do card.
   */
  /**
   * Faixa do status — célula EXTERNA de 5px de largura. Como a tabela
   * externa tem `border-spacing:0 1px`, esta célula cola na coluna ID
   * (sem fio entre elas). Carrega o arredondamento esquerdo do card.
   * Altura = altura da row (regra HTML padrão de cells de mesma row).
   */
  private statusBarCellHtml(statusBg: string): string {
    return ''
      + `<td bgcolor="${statusBg}" width="5" style="`
      +   `width:5px;min-width:5px;padding:0;font-size:0;line-height:0;`
      +   `background-color:${statusBg};`
      +   `border-top-left-radius:6px;border-bottom-left-radius:6px;`
      + `">&nbsp;</td>`;
  }

  /**
   * Coluna do ID — `#NNN` em cima + círculo do status embaixo.
   * Faixa de cor é uma CÉLULA EXTERNA separada (vide
   * `statusBarCellHtml` + composição em `submissionRowHtml`) —
   * `background-image:linear-gradient` é stripado por vários destinos
   * rich-text. Como a tabela externa usa `border-spacing:0 1px` (zero
   * horizontal), faixa e ID ficam coladas naturalmente.
   */
  private idCellHtml(idText: string, statusKey: string, statusCircleBg: string): string {
    const circle = this.statusCircleHtml(statusKey, statusCircleBg);
    return ''
      + `<td bgcolor="${this.BG_CARD}" align="center" width="80" style="`
      +   `font-family:${this.FONT_STACK};font-size:13px;color:${this.TEXT_BASE};`
      +   `padding:10px 10px;`
      +   `vertical-align:middle;text-align:center;`
      +   `white-space:nowrap;font-weight:400;`
      +   `background-color:${this.BG_CARD};`
      + `">`
      + `<div style="text-align:center;font-family:${this.FONT_STACK};font-size:13px;color:${this.TEXT_BASE};line-height:1.4;">${this.esc(idText)}</div>`
      + `<div style="text-align:center;margin-top:6px;line-height:0;">${circle}</div>`
      + `</td>`;
  }

  /**
   * Coluna principal — gray-125 bg + arredondamento à DIREITA (passou a
   * ser a última célula visual, agora que o chip foi removido).
   */
  private mainCellHtml(inner: string, lastVisual: boolean): string {
    const radius = '';   // radius do canto direito é dado pela trailing cell, não pelo content cell
    return ''
      + `<td bgcolor="${this.BG_CARD}" width="100%" style="`
      +   `font-family:${this.FONT_STACK};font-size:13px;color:${this.TEXT_BASE};`
      +   `padding:12px 16px;vertical-align:top;`
      +   `background-color:${this.BG_CARD};`
      +   `width:100%;`
      +   radius
      + `">`
      + inner
      + `</td>`;
  }

  /**
   * Pequeno círculo colorido com o ícone MATERIAL SYMBOLS do status —
   * réplica fiel do `.snc-status-icon-only` do card.
   *
   * O círculo é um `<span inline-block>` com box-sizing border-box (forma
   * sobrevive a destinos rich-text); o ícone é SVG inline com o MESMO
   * path do Material Symbol usado no card (thumb_up, check, priority_high,
   * mail, schedule, edit, person, etc.) — assim os símbolos são exata-
   * mente os do sistema, não aproximações unicode.
   *
   *   - border embossed (top+left claros, right+bottom escuros)
   *   - box-shadow: 0 1px 2px rgba(0,0,0,.55)
   *   - border-radius: 50%
   *   - SVG 14×14, fill #434343 (mesma cor `$sidebar-label-color` do chip)
   */
  private statusCircleHtml(statusKey: string, statusBg: string): string {
    const path = this.STATUS_ICON_PATH[statusKey];
    // SVG `display:block; margin:3px auto` centra geometricamente o ícone
    // 14×14 dentro do círculo 22×22 (com bordas de 1px each side, sobra
    // 20×20 interno; (20-14)/2 = 3px de margem em cada lado). `vertical-
    // align:middle` em inline-svg posiciona pelo baseline tipográfico — o
    // que coloca o ícone baixo. `display:block + margin:auto` ignora
    // baseline e usa centro GEOMÉTRICO.
    const inner = path
      ? `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 -960 960 960" style="display:block;margin:3px auto;"><path d="${path}" fill="${this.CHIP_TEXT}"/></svg>`
      : `<span style="font-family:${this.FONT_STACK};font-size:11px;font-weight:700;color:${this.CHIP_TEXT};line-height:20px;">·</span>`;
    return `<span style="`
      +   `display:inline-block;`
      +   `box-sizing:border-box;`
      +   `width:22px;height:22px;`
      +   `background-color:${statusBg};`
      +   `border-radius:50%;`
      +   `border-top:1px solid rgba(255,255,255,0.6);`
      +   `border-left:1px solid rgba(255,255,255,0.6);`
      +   `border-right:1px solid rgba(0,0,0,0.12);`
      +   `border-bottom:1px solid rgba(0,0,0,0.12);`
      +   `box-shadow:0 1px 2px rgba(0,0,0,0.55);`
      +   `vertical-align:middle;`
      + `">`
      + inner
      + `</span>`;
  }

  // ─── Conteúdo da coluna principal ───────────────────────────────────

  private submissionMainContent(s: any): string {
    const titleLink = this.absoluteUrl(`/submissions/${s?.id ?? ''}`);
    const title = this.esc(s?.title ?? '');
    const authors = this.formatAuthors(s?.authors);
    const trackLine = s?.track?.name
      ? `<div style="font-family:${this.FONT_STACK};font-size:13px;color:${this.TEXT_BASE};margin-top:3px;line-height:1.35;"><span style="color:${this.TEXT_MUTED};margin-right:4px;">Trilha:</span>${this.esc(s.track.name)}</div>`
      : '';
    return ''
      // Título: tokens 1:1 com `.snc-title` ([submission-card-new.component.scss:164]):
      //   font-size: 1rem (16px) · font-weight: 600 · color: $accent-background
      //   letter-spacing: -0.01em · line-height: 1.35
      //
      // Estrutura defensiva contra strip de destinos rich-text:
      //   - <a> só carrega color + text-decoration (atributos de link)
      //   - <span> wrapper interno carrega font-size + font-family +
      //     line-height + letter-spacing (destinos strippam font-size em
      //     <a>, mas mantêm em <span>)
      //   - <b> garante peso bold mesmo se font-weight inline for descartado
      + `<div style="margin-bottom:2px;line-height:1.35;">`
      + `<a href="${titleLink}" style="color:${this.TEXT_ACCENT};text-decoration:none;">`
      + `<span style="font-family:${this.FONT_STACK};font-size:16px;color:${this.TEXT_ACCENT};letter-spacing:-0.01em;line-height:1.35;">`
      + `<b style="font-weight:600;font-size:16px;">${title}</b>`
      + `</span></a></div>`
      + (authors ? `<div style="font-family:${this.FONT_STACK};font-size:13px;color:${this.TEXT_BASE};line-height:1.4;">${authors}</div>` : '')
      + trackLine;
  }

  /**
   * Coluna "Sessão de apresentação" — célula separada (não inline na main),
   * espelha `.snc-session-col` do card (entre conteúdo principal e
   * arquivos). Só renderiza se houver `session.title` OU `session.start`,
   * mesma condição de `submission-card-new.component.html:49`.
   * Mostra título, data, horário e sala (apenas os campos disponíveis).
   */
  private sessionCellHtml(session: any, timezone: string | undefined, lastVisual: boolean): string {
    // Formato espelha o `datePipe` do sistema:
    //   - data:  "dd 'de' MMM yyyy" (pt) ou "MMM dd, yyyy" (en)
    //   - hora:  "H:mm - H:mm UTC±N" (start sem TZ, end com TZ — igual ao
    //     template do card em submission-card-new.component.html:53).
    // `white-space:nowrap` + `text-align:center` repetidos em CADA <div>
    // interno — destinos rich-text (Notion/Word) não herdam essas regras
    // do <td> pros filhos.
    const lines: string[] = [];
    if (session.title) {
      lines.push(`<div style="text-align:center;font-weight:500;color:${this.TEXT_BASE};line-height:1.35;white-space:nowrap;">${this.esc(session.title)}</div>`);
    }
    if (session.start) {
      const date = this.formatSessionDate(session.start);
      lines.push(`<div style="text-align:center;color:${this.TEXT_BASE};line-height:1.35;margin-top:2px;white-space:nowrap;">${this.esc(date)}</div>`);
      if (session.end) {
        const t1 = this.formatSessionTime(session.start, timezone, /*includeTz*/ false);
        const t2 = this.formatSessionTime(session.end, timezone, /*includeTz*/ true);
        lines.push(`<div style="text-align:center;color:${this.TEXT_MUTED};line-height:1.35;white-space:nowrap;">${this.esc(t1)} - ${this.esc(t2)}</div>`);
      }
    }
    if (session.room) {
      lines.push(`<div style="text-align:center;color:${this.TEXT_BASE};line-height:1.35;margin-top:2px;white-space:nowrap;"><span style="color:${this.TEXT_MUTED};margin-right:4px;">Sala:</span>${this.esc(session.room)}</div>`);
    }
    const radius = '';   // radius do canto direito é dado pela trailing cell, não pelo content cell
    return ''
      // Shrink-to-fit (mesmo padrão do idCellHtml):
      //   width="1" + style="width:1px" + white-space:nowrap
      //   → cell encolhe pro mínimo possível; como o conteúdo não pode
      //     quebrar (nowrap), expande SÓ até caber título/data/hora/sala.
      + `<td bgcolor="${this.BG_CARD}" align="center" width="150" style="`
      +   `font-family:${this.FONT_STACK};font-size:13px;color:${this.TEXT_BASE};`
      +   `padding:10px 10px;vertical-align:top;text-align:center;`
      +   `background-color:${this.BG_CARD};`
      +   `white-space:nowrap;`
      +   radius
      + `">`
      + lines.join('')
      + `</td>`;
  }

  private reviewMainContent(r: any): string {
    const sub = r?.submission ?? {};
    const titleLink = this.absoluteUrl(`/submissions/${sub?.id ?? ''}`);
    const title = this.esc(sub?.title ?? '');
    const authors = this.formatAuthors(sub?.authors);
    const trackLine = sub?.track?.name
      ? `<div style="font-family:${this.FONT_STACK};font-size:13px;color:${this.TEXT_BASE};margin-top:3px;line-height:1.35;"><span style="color:${this.TEXT_MUTED};margin-right:4px;">Trilha:</span>${this.esc(sub.track.name)}</div>`
      : '';
    // Prazo vira linha própria logo abaixo da Trilha (espelha o card,
    // onde o "Prazo para revisão:" virou um `.cv-eb-context` no main).
    const dueBlock = r?.dueAt
      ? `<div style="font-family:${this.FONT_STACK};font-size:13px;color:${this.TEXT_BASE};margin-top:3px;line-height:1.35;"><span style="color:${this.TEXT_MUTED};margin-right:4px;">${this.isMeTheReviewer(r) ? 'Prazo para sua revisão:' : 'Prazo para revisão:'}</span><b style="font-weight:500;">${this.formatSessionDate(r.dueAt)}</b></div>`
      : '';
    const reviewLine = this.reviewLineHtml(r);
    return ''
      + `<div style="margin-bottom:2px;line-height:1.35;">`
      + `<a href="${titleLink}" style="color:${this.TEXT_ACCENT};text-decoration:none;">`
      + `<span style="font-family:${this.FONT_STACK};font-size:16px;color:${this.TEXT_ACCENT};letter-spacing:-0.01em;line-height:1.35;">`
      + `<b style="font-weight:600;font-size:16px;">${title}</b>`
      + `</span></a></div>`
      + (authors ? `<div style="font-family:${this.FONT_STACK};font-size:13px;color:${this.TEXT_BASE};line-height:1.4;">${authors}</div>` : '')
      + trackLine
      + dueBlock
      + reviewLine;
  }

  /**
   * Linha de info de REVISÃO (espelha `.cv-eb-review-line` do card).
   * Stage chip · Ownership chip (opcional) · Prazo: data. Discussão e
   * Rebuttal saíram daqui e moram na meta cell (vide `reviewMetaCellHtml`).
   */
  private reviewLineHtml(r: any): string {
    const status = (r?.status ?? '').toString().toUpperCase();
    const reviewStatusKey = status.toLowerCase();
    const reviewStatusBg = this.REVIEW_STATUS_BG[reviewStatusKey] || '#DDDDDD';

    // Pill template — espelha o `.span-pill` do chair (Lista de
    // Submissões): radius 4px, weight 400, padding leve. Cor de fundo
    // por status via mapa REVIEW_STATUS_BG, ícone SVG do status
    // embedado (Material Symbols FILLED, viewBox 0 -960 960 960).
    const iconPath = this.STATUS_ICON_PATH[reviewStatusKey];
    const iconSvg = iconPath
      ? `<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 -960 960 960" style="display:inline-block;vertical-align:middle;margin-right:4px;"><path d="${iconPath}" fill="${this.CHIP_TEXT}"/></svg>`
      : '';
    const stageChip = (label: string): string =>
      `<span style="`
      + `display:inline-block;padding:2px 8px;border-radius:4px;`
      + `font-family:${this.FONT_STACK};font-size:11px;font-weight:400;line-height:1.3;`
      + `background-color:${reviewStatusBg};color:${this.CHIP_TEXT};`
      + `white-space:nowrap;vertical-align:middle;`
      + `">${iconSvg}${this.esc(label)}</span>`;

    // Prazo NÃO entra mais aqui — foi promovido a linha própria no
    // `reviewMainContent`, logo abaixo da Trilha.
    const wrap = (content: string): string =>
      `<div style="font-family:${this.FONT_STACK};font-size:13px;color:${this.TEXT_BASE};margin-top:10px;line-height:1.5;">`
      + content
      + `</div>`;

    // ── Frases narrativas — espelha `cv-eb-review-phrase` do card. ──
    // Precedência: delegação por mim > status NOTIFIED/CONFIRMED > fallback.

    // DELEGAÇÃO POR MIM — vence sobre o status cru.
    const ownState_top = this.getOwnershipState(r);
    if (ownState_top === 'delegated') {
      const other = this.getOwnershipOther(r);
      const otherName = other?.name ? `<b>${this.esc(other.name)}</b>` : '';
      const date = r?.assignedAt
        ? ` em <b>${this.esc(this.formatSessionDate(r.assignedAt))}</b>`
        : '';
      const phrase1 = `Você delegou${date} para ${otherName} a revisão desta submissão.`;

      // 2º segmento — descreve o estado atual do DELEGATE.
      let phrase2 = '';
      if (status === 'NOTIFIED') {
        phrase2 = ` Ele(a) foi ${stageChip('Convidado(a)')} mas ainda não confirmou se vai revisar.`;
      } else if (status === 'CONFIRMED') {
        phrase2 = ` Ele(a) ${stageChip('Confirmou')} que vai revisar a submissão.`;
      } else if (status === 'DECLINED') {
        phrase2 = ` Ele(a) ${stageChip('Declinou')} do convite e não irá revisar esta submissão.`;
      }

      return wrap(phrase1 + phrase2);
    }

    if (status === 'NOTIFIED') {
      const by = r?.assignedBy?.name
        ? ` por <b>${this.esc(r.assignedBy.name)}</b>`
        : '';
      const phrase = `Você foi ${stageChip('Convidado(a)')}${by} a revisar esta submissão e agora precisa aceitar ou declinar este convite.`;
      return wrap(phrase);
    }
    if (status === 'CONFIRMED') {
      const phrase = `Você ${stageChip('Confirmou')} que irá revisar esta submissão.`;
      return wrap(phrase);
    }

    // ── Fallback: chip stage + ownership (par com dot). ──
    const chips: string[] = [];
    const stageLabel = this.formatStatusLabel(r?.status);
    if (stageLabel) { chips.push(stageChip(stageLabel)); }
    const other = this.getOwnershipOther(r);
    const ownState = this.getOwnershipState(r);
    if (other && ownState) {
      const prefix = ownState === 'delegated' ? 'Delegada → ' : 'Recebida ← ';
      chips.push(
        `<span style="`
        + `display:inline-block;padding:2px 9px;border-radius:999px;`
        + `font-family:${this.FONT_STACK};font-size:11px;font-weight:500;line-height:1.4;`
        + `background-color:${this.REBUTTAL_BG};color:${this.TEXT_BASE};`
        + `border:1px solid ${this.REBUTTAL_BORDER};white-space:nowrap;`
        + `">${this.esc(prefix + other.name)}</span>`
      );
    }
    if (!chips.length) { return ''; }
    return wrap(chips.join(' '));
  }

  private isMeTheReviewer(r: any): boolean {
    return r?.user?.id === this.infoService?.user?.id;
  }

  /** Mesma lógica do `review-card-new.component.ts > getOwnershipState`. */
  private getOwnershipState(r: any): 'delegated' | 'received' | null {
    if (!r?.user) { return null; }
    if (!this.isMeTheReviewer(r)) { return 'delegated'; }
    if (r?.isFirstAssign === false && r?.assignedBy) { return 'received'; }
    return null;
  }

  /** Mesma lógica do `review-card-new.component.ts > getOwnershipOther`. */
  private getOwnershipOther(r: any): { name: string, aff?: string } | null {
    const state = this.getOwnershipState(r);
    if (!state) { return null; }
    const other = state === 'delegated' ? r?.user : r?.assignedBy;
    if (!other?.name) { return null; }
    return { name: other.name, aff: other?.profile?.affiliation?.acronym };
  }

  /**
   * Coluna meta de revisão — Discussão: N (sempre) + chip Rebuttal
   * (opcional). Stage e Ownership ficam na main cell via `reviewLineHtml`.
   * Espelha `.cv-eb-meta-col` do card.
   */
  private reviewMetaCellHtml(r: any): string {
    const discussionCount = (r?.submission?.discussionMessages?.length) || 0;
    const parts: string[] = [];
    parts.push(
      `<div style="text-align:center;font-family:${this.FONT_STACK};font-size:13px;color:${this.TEXT_BASE};line-height:1.35;white-space:nowrap;">`
      + `<span style="color:${this.TEXT_MUTED};margin-right:4px;">Discussão:</span>`
      + `<b style="font-weight:500;">${discussionCount}</b>`
      + `</div>`
    );
    if (r?.submission?.rebuttal) {
      parts.push(
        `<div style="text-align:center;margin-top:6px;line-height:0;white-space:nowrap;">`
        + `<span style="`
        +   `display:inline-block;padding:2px 9px;border-radius:999px;`
        +   `font-family:${this.FONT_STACK};font-size:11px;font-weight:500;line-height:1.4;`
        +   `background-color:${this.REBUTTAL_BG};color:${this.TEXT_BASE};`
        +   `border:1px solid ${this.REBUTTAL_BORDER};white-space:nowrap;`
        + `">Rebuttal</span>`
        + `</div>`
      );
    }
    return ''
      + `<td bgcolor="${this.BG_CARD}" align="center" width="140" style="`
      +   `font-family:${this.FONT_STACK};font-size:13px;color:${this.TEXT_BASE};`
      +   `padding:10px 10px;vertical-align:middle;text-align:center;`
      +   `white-space:nowrap;`
      +   `background-color:${this.BG_CARD};`
      + `">`
      + parts.join('')
      + `</td>`;
  }

  // ─── Cabeçalho de evento ────────────────────────────────────────────

  private eventHeaderHtml(event: any): string {
    if (!event) { return ''; }
    const acronym = this.esc(event.acronym ?? '');
    const year = event.year ? ` ${this.esc(event.year)}` : '';
    const name = event.name ? ` - ${this.esc(event.name)}` : '';
    return `<h2 style="font-family:${this.FONT_STACK};font-size:18px;font-weight:600;color:${this.EVENT_TITLE};margin:18px 0 8px;letter-spacing:-0.015em;line-height:1.3;">`
      + `${acronym}${year}${name}</h2>`;
  }

  // ─── Helpers ────────────────────────────────────────────────────────

  private formatAuthors(authors: any[]): string {
    if (!authors?.length) { return ''; }
    return authors.map(a => {
      const name = a?.user?.name;
      if (!name) { return ''; }
      const aff = a?.user?.profile?.affiliation?.acronym;
      return aff ? `${this.esc(name)} (${this.esc(aff)})` : this.esc(name);
    }).filter(Boolean).join(', ');
  }

  private formatStatusLabel(status: any): string {
    if (!status) { return ''; }
    const raw = String(status);
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  }

  private formatDate(d: any): string {
    if (!d) { return ''; }
    try {
      const date = (d instanceof Date) ? d : new Date(d);
      return date.toLocaleDateString();
    } catch { return ''; }
  }

  /**
   * Formata data de sessão como o `datePipe: 'date'`:
   *   - pt-BR: `"dd 'de' MMM yyyy"` → "07 de mar 2021"
   *   - en:    `'MMM dd, yyyy'`     → "Mar 07, 2021"
   * NÃO recebe timezone — o card também não passa fuso pro `datePipe: 'date'`
   * (vide submission-card-new.component.html:51). Usa LOCAL — qualquer
   * conversão de fuso aqui desloca o dia em relação ao que o card mostra.
   */
  private formatSessionDate(value: any): string {
    if (!value) { return ''; }
    try {
      const m = moment(value);
      const isPt = this.isPtBr();
      const day = m.format('DD');
      const monthIdx = m.month();
      const month = (isPt ? this.MONTHS_PT : this.MONTHS_EN)[monthIdx];
      const year = m.format('YYYY');
      return isPt ? `${day} de ${month} ${year}` : `${month} ${day}, ${year}`;
    } catch { return ''; }
  }

  /**
   * Formata horário como o `datePipe: 'shortTime'` (sem TZ) ou
   * `'shortTimeZone'` (com sufixo `UTC±N`):
   *   - pt-BR: `'H:mm'` ou `'H:mm UTC±N'`
   *   - en:    `'h:mm a'` ou `'h:mm a UTC±N'`
   * O sufixo UTC vem do offset do timezone do evento (ex.: `-03:00` → `UTC-3`).
   */
  private formatSessionTime(value: any, timezone?: string, includeTz?: boolean): string {
    if (!value) { return ''; }
    try {
      const m = timezone ? moment(value).tz(timezone) : moment(value);
      const isPt = this.isPtBr();
      let formatted = m.format(isPt ? 'H:mm' : 'h:mm A');
      if (includeTz) {
        const offset = m.format('Z'); // ±HH:MM
        formatted += ` UTC${this.shortOffset(offset)}`;
      }
      return formatted;
    } catch { return ''; }
  }

  private shortOffset(offset: string): string {
    // "-03:00" → "-3" · "+05:30" → "+5:30"
    const match = offset.match(/([+-])(\d{2}):(\d{2})/);
    if (!match) { return offset; }
    const sign = match[1];
    const hours = parseInt(match[2], 10);
    const minutes = match[3];
    return minutes === '00' ? `${sign}${hours}` : `${sign}${hours}:${minutes}`;
  }

  private isPtBr(): boolean {
    const lang = (this.translateService?.currentLang || '').toLowerCase();
    return lang.startsWith('pt');
  }

  private absoluteUrl(path: string): string {
    try {
      const origin = window?.location?.origin ?? '';
      const safePath = path.startsWith('/') ? path : `/${path}`;
      return `${origin}${safePath}`;
    } catch { return path; }
  }

  private esc(v: any): string {
    return String(v ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // ─── Plain text fallback ────────────────────────────────────────────

  private buildSubmissionsText(items: any[]): string {
    return items.map(s => {
      const id = s?.id ?? '';
      const title = s?.title ?? '';
      const authors = (s?.authors || []).map((a: any) => a?.user?.name).filter(Boolean).join(', ');
      const event = [s?.event?.acronym, s?.event?.year].filter(Boolean).join(' ');
      return `#${id}\t${title}\t${authors}\t${event}\t${s?.track?.name ?? ''}\t${s?.status ?? ''}`;
    }).join('\n');
  }

  private buildReviewsText(items: any[]): string {
    return items.map(r => {
      const sub = r?.submission ?? {};
      const event = [sub?.event?.acronym, sub?.event?.year].filter(Boolean).join(' ');
      const due = r?.dueAt ? new Date(r.dueAt).toLocaleDateString() : '';
      return `#${sub?.id ?? ''}\t${sub?.title ?? ''}\t${event}\t${sub?.track?.name ?? ''}\t${r?.status ?? ''}\t${due}`;
    }).join('\n');
  }

  // ─── Clipboard write (HTML + text fallback) ─────────────────────────

  private async writeBoth(html: string, text: string): Promise<boolean> {
    try {
      const ClipboardItemCtor = (window as any).ClipboardItem;
      if (navigator.clipboard && ClipboardItemCtor) {
        const item = new ClipboardItemCtor({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        });
        await (navigator.clipboard as any).write([item]);
        return true;
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (_) { /* fall through */ }
    // Sem fallback de textarea+execCommand: o `focus()` daquela abordagem
    // SEMPRE força scroll-into-view do browser em alguns destinos (mesmo
    // com `preventScroll:true` ignorado por Safari/macOS). Browsers
    // modernos (Mac Safari, Chrome, Firefox) suportam Clipboard API —
    // o try-catch acima já cobre eles. Se falhar, copy simplesmente
    // retorna false (não há paste, mas a página NÃO pula).
    return false;
  }
}
